import React, { useState, useEffect, useMemo } from "react";
import { Search, Filter, FileSpreadsheet, Plus, Pencil, Trash2, X, Sparkles } from "lucide-react";
import { C, eur, pct0 } from "../igualdad-data.js";
import { Status } from "../igualdad-components.jsx";
import {
  apiGetAll, apiGet, apiPost, apiPut, apiDelete, mapPersona, downloadCsv,
} from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";
import { navigate } from "../router.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Personas y Puestos
   ============================================================ */

function blankForm() {
  return { codigo: "", nombre: "", sexo: "M", puesto_id: "", departamento_id: "", centro_id: "", razon: "", jornada: 1, fecha_alta: "", fijo_anual: 0, variable_anual: 0 };
}

/* ---- Personas ---- */
function IgualdadPersonas() {
  const { session, fireToast } = useIgualdad();
  const politicaId = session.politicaId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [people, setPeople] = useState([]);
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState({ puestos: [], departamentos: [], centros: [] });

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    if (!politicaId) { setLoading(false); setPeople([]); return; }
    setLoading(true); setError(null);
    try {
      const [list, pu, de, ce] = await Promise.all([
        apiGetAll("/personas", { politica_id: politicaId }),
        apiGetAll("/puestos", { politica_id: politicaId }),
        apiGetAll("/departamentos"),
        apiGetAll("/centros-trabajo"),
      ]);
      setPeople(list.map(mapPersona));
      setOpts({ puestos: pu, departamentos: de, centros: ce });
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [politicaId]);

  const rows = useMemo(() =>
    people.filter((p) => `${p.nombre} ${p.puesto} ${p.depto} ${p.codigo}`.toLowerCase().includes(q.toLowerCase())),
    [people, q]);

  // Los agregados de quien deriva de sus situaciones no se editan aquí: el
  // backend responde 409 (F2.B2). En el alta nunca aplica, porque una persona
  // recién creada no tiene situaciones.
  const derivaEditada = Boolean(editing?.deriva_de_situaciones);

  function openNew() { setEditing(null); setForm(blankForm()); setFormErr(null); setModal(true); }

  async function openEdit(p) {
    setFormErr(null);
    try {
      const res = await apiGet(`/personas/${p.id}`);
      const r = res.data || res;
      setEditing(r);
      setForm({
        codigo: r.codigo, nombre: r.nombre, sexo: r.sexo,
        puesto_id: r.puesto_id ?? "", departamento_id: r.departamento_id ?? "", centro_id: r.centro_id ?? "",
        razon: r.razon ?? "", jornada: r.jornada, fecha_alta: r.fecha_alta ?? "",
        fijo_anual: r.fijo_anual, variable_anual: r.variable_anual ?? 0,
      });
      setModal(true);
    } catch (e) { fireToast(e.message || "No se pudo cargar la persona."); }
  }

  function payload() {
    const f = form;
    return {
      politica_id: politicaId,
      codigo: f.codigo, nombre: f.nombre, sexo: f.sexo,
      puesto_id: f.puesto_id === "" ? null : Number(f.puesto_id),
      departamento_id: f.departamento_id === "" ? null : Number(f.departamento_id),
      centro_id: f.centro_id === "" ? null : Number(f.centro_id),
      razon: f.razon === "" ? null : f.razon,
      jornada: Number(f.jornada),
      fecha_alta: f.fecha_alta === "" ? null : f.fecha_alta,
      fijo_anual: Number(f.fijo_anual),
      variable_anual: f.variable_anual === "" ? null : Number(f.variable_anual),
    };
  }

  async function save() {
    if (saving) return;
    setSaving(true); setFormErr(null);
    try {
      if (editing) await apiPut(`/personas/${editing.id}`, payload());
      else await apiPost("/personas", payload());
      setModal(false);
      fireToast(editing ? "Persona actualizada." : "Persona creada.");
      await load();
    } catch (e) {
      setFormErr(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo guardar."));
    } finally { setSaving(false); }
  }

  async function remove() {
    if (deleting || !confirmDel) return;
    setDeleting(true);
    try {
      await apiDelete(`/personas/${confirmDel.id}`);
      fireToast("Persona eliminada.");
      setConfirmDel(null);
      await load();
    } catch (e) { fireToast(e.message || "No se pudo eliminar."); }
    finally { setDeleting(false); }
  }

  function exportCsv() {
    downloadCsv(
      "personas.csv",
      ["Código", "Persona", "Sexo", "Puesto", "Departamento", "% Jornada", "Salario fijo", "Total", "Puntos VPT"],
      rows.map((p) => [p.codigo, p.nombre, p.sexo === "M" ? "Mujer" : "Hombre", p.puesto, p.depto, Math.round(p.jornada * 100), Math.round(p.fijoReal), Math.round(p.totalReal), p.pts])
    );
    fireToast(`Exportadas ${rows.length} personas a CSV.`);
  }

  return (
    <div className="rb-page rb-perso">
      <h1 className="rb-h1">Personas trabajadoras</h1>
      <div className="rb-toolbar">
        <div className="rb-search"><Search size={15} /><input placeholder="Buscar por nombre, puesto o código…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <button className="rb-btn-soft" onClick={openNew}><Plus size={14} /> Nueva</button>
        <button className="rb-btn-ghost"><Filter size={14} /> Filtros avanzados</button>
        <div className="rb-toolbar-r">
          <span className="rb-count">{rows.length} personas</span>
          <button className="rb-btn-ghost" onClick={exportCsv}><FileSpreadsheet size={14} /> Excel</button>
        </div>
      </div>

      {(loading || error) ? <Status loading={loading} error={error} /> : (
        <div className="rb-card rb-card-flat">
          <table className="rb-table">
            <thead><tr>
              <th className="rb-l">Código</th><th className="rb-l">Persona</th><th>Sexo</th><th className="rb-l">Puesto</th>
              <th className="rb-l">Departamento</th><th>% Jornada</th><th>Salario fijo</th><th>Total</th><th>Puntos VPT</th><th className="rb-actions-h"></th>
            </tr></thead>
            <tbody>
              {rows.slice(0, 40).map((p) => (
                <tr key={p.id}>
                  <td className="rb-l rb-mono">{p.codigo}</td>
                  <td className="rb-l rb-strong">
                    <button className="rb-link" onClick={() => navigate(`/igualdad/personas/${p.id}`)}>{p.nombre}</button>
                  </td>
                  <td><span className="rb-sex" style={{ background: (p.sexo === "M" ? C.M : C.H) + "22", color: p.sexo === "M" ? C.brand : C.brandHover }}>{p.sexo === "M" ? "Mujer" : "Hombre"}</span></td>
                  <td className="rb-l">{p.puesto}</td>
                  <td className="rb-l rb-soft">{p.depto}</td>
                  <td className="rb-mono">{pct0(p.jornada * 100)}</td>
                  <td className="rb-mono">{eur(p.fijoReal)}</td>
                  <td className="rb-mono">{eur(p.totalReal)}</td>
                  <td><span className="rb-pts">{p.pts}</span></td>
                  <td className="rb-actions">
                    <button className="rb-ico-btn" title="Editar" onClick={() => openEdit(p)}><Pencil size={15} /></button>
                    <button className="rb-ico-btn rb-danger" title="Eliminar" onClick={() => setConfirmDel(p)}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td className="rb-l rb-soft" colSpan={10}>No se han encontrado personas.</td></tr>}
            </tbody>
          </table>
          {rows.length > 40 && <div className="rb-tablefoot">Mostrando 40 de {rows.length} · usa la búsqueda para acotar</div>}
        </div>
      )}

      {modal && (
        <div className="rb-modal-bg" onClick={() => setModal(false)}>
          <div className="rb-modal rb-modal-wide-personas" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">{editing ? "Editar persona" : "Nueva persona"}</div><div className="rb-modal-s">Política {session.politica?.ejercicio}</div></div>
              <button className="rb-ico" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b">
              <div className="rb-grid2-form">
                <div><label>Código</label><input className="rb-inp" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="p. ej. 100023" /></div>
                <div><label>Nombre</label><input className="rb-inp" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Apellido, Nombre" /></div>
                <div><label>Sexo</label><select value={form.sexo} onChange={(e) => setForm({ ...form, sexo: e.target.value })}><option value="M">Mujer</option><option value="H">Hombre</option></select></div>
                <div><label>Puesto</label><select value={form.puesto_id} onChange={(e) => setForm({ ...form, puesto_id: e.target.value })}><option value="">— Selecciona —</option>{opts.puestos.map((o) => <option key={o.id} value={o.id}>{o.nombre}</option>)}</select></div>
                <div><label>Departamento</label><select value={form.departamento_id} onChange={(e) => setForm({ ...form, departamento_id: e.target.value })}><option value="">— Sin departamento —</option>{opts.departamentos.map((o) => <option key={o.id} value={o.id}>{o.nombre}</option>)}</select></div>
                <div><label>Centro de trabajo</label><select value={form.centro_id} onChange={(e) => setForm({ ...form, centro_id: e.target.value })}><option value="">— Sin centro —</option>{opts.centros.map((o) => <option key={o.id} value={o.id}>{o.nombre}</option>)}</select></div>
                <div><label>% Jornada (0,01–1)</label><input className="rb-inp" type="number" step="0.01" min="0.01" max="1" disabled={derivaEditada} value={form.jornada} onChange={(e) => { const v = e.target.value; const n = parseFloat(v); setForm({ ...form, jornada: Number.isNaN(n) ? v : n }); }} /></div>
                <div><label>Fecha de alta</label><input className="rb-inp" type="date" value={form.fecha_alta} onChange={(e) => setForm({ ...form, fecha_alta: e.target.value })} /></div>
                <div><label>Retribución fija anual (€)</label><input className="rb-inp" type="number" min="0" disabled={derivaEditada} value={form.fijo_anual} onChange={(e) => { const v = e.target.value; const n = parseFloat(v); setForm({ ...form, fijo_anual: Number.isNaN(n) ? v : n }); }} /></div>
                <div><label>Retribución variable anual (€)</label><input className="rb-inp" type="number" min="0" disabled={derivaEditada} value={form.variable_anual} onChange={(e) => { const v = e.target.value; const n = parseFloat(v); setForm({ ...form, variable_anual: Number.isNaN(n) ? v : n }); }} /></div>
                {derivaEditada ? (
                  <div className="rb-grid2-full">
                    <p className="rb-note">
                      La jornada y los importes se calculan a partir de las situaciones de esta persona.
                      Ábrela para editarlas.
                    </p>
                  </div>
                ) : null}
                <div className="rb-grid2-full"><label>Razón social</label><input className="rb-inp" value={form.razon} onChange={(e) => setForm({ ...form, razon: e.target.value })} placeholder="Empresa Pyme" /></div>
              </div>
              {formErr && <div className="rb-formerr">{formErr}</div>}
            </div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
              <button className="rb-btn" disabled={saving} onClick={save}>{saving ? "Guardando…" : (editing ? "Guardar cambios" : "Crear persona")}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="rb-modal-bg" onClick={() => setConfirmDel(null)}>
          <div className="rb-modal rb-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">Eliminar persona</div><div className="rb-modal-s">Esta acción no se puede deshacer.</div></div>
              <button className="rb-ico" onClick={() => setConfirmDel(null)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b"><p className="rb-soft">¿Seguro que quieres eliminar a <b>{confirmDel.nombre}</b> ({confirmDel.codigo})?</p></div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setConfirmDel(null)}>Cancelar</button>
              <button className="rb-btn rb-btn-danger" disabled={deleting} onClick={remove}>{deleting ? "Eliminando…" : "Eliminar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Puestos ---- */
function IgualdadPuestos() {
  const { session, fireToast } = useIgualdad();
  const politicaId = session.politicaId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [puestos, setPuestos] = useState([]);
  const [q, setQ] = useState("");

  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState(null);
  const [form, setForm] = useState({ nombre: "", bench: "", pts: 300 });
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    if (!politicaId) { setLoading(false); setPuestos([]); return; }
    setLoading(true); setError(null);
    try {
      setPuestos(await apiGetAll("/puestos", { politica_id: politicaId }));
    } catch (e) { setError(e.message || true); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [politicaId]);

  const rows = useMemo(() =>
    puestos
      .filter((p) => `${p.nombre} ${p.bench}`.toLowerCase().includes(q.toLowerCase()))
      .slice()
      .sort((a, b) => (b.pts || 0) - (a.pts || 0)),
    [puestos, q]);

  function openNew() { setEditing(null); setForm({ nombre: "", bench: "", pts: 300 }); setFormErr(null); setModal(true); }
  function openEdit(p) { setEditing(p); setForm({ nombre: p.nombre, bench: p.bench, pts: p.pts ?? 0 }); setFormErr(null); setModal(true); }

  async function save() {
    if (saving) return;
    setSaving(true); setFormErr(null);
    try {
      const body = { politica_id: politicaId, nombre: form.nombre, bench: form.bench, pts: Number(form.pts) };
      if (editing) await apiPut(`/puestos/${editing.id}`, body);
      else await apiPost("/puestos", body);
      setModal(false);
      fireToast(editing ? "Puesto actualizado." : "Puesto creado.");
      await load();
    } catch (e) {
      setFormErr(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo guardar."));
    } finally { setSaving(false); }
  }

  async function remove() {
    if (deleting || !confirmDel) return;
    setDeleting(true);
    try {
      await apiDelete(`/puestos/${confirmDel.id}`);
      fireToast("Puesto eliminado.");
      setConfirmDel(null);
      await load();
    } catch (e) { fireToast(e.message || "No se pudo eliminar."); }
    finally { setDeleting(false); }
  }

  function exportCsv() {
    downloadCsv(
      "puestos.csv",
      ["Puesto", "Benchmark", "Grupo", "Nº personas", "Puntos VPT"],
      rows.map((r) => [r.nombre, r.bench, r.grupo, r.n_personas ?? 0, r.pts])
    );
    fireToast(`Exportados ${rows.length} puestos a CSV.`);
  }

  return (
    <div className="rb-page">
      <h1 className="rb-h1">Puestos de trabajo</h1>
      <div className="rb-toolbar">
        <div className="rb-search"><Search size={15} /><input placeholder="Buscar puesto…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <button className="rb-btn-soft" onClick={openNew}><Plus size={14} /> Nuevo</button>
        <div className="rb-toolbar-r">
          <span className="rb-count">{rows.length} puestos</span>
          <button className="rb-btn-ghost" onClick={exportCsv}><FileSpreadsheet size={14} /> Excel</button>
        </div>
      </div>

      {(loading || error) ? <Status loading={loading} error={error} /> : (
        <div className="rb-card rb-card-flat">
          <table className="rb-table">
            <thead><tr><th className="rb-l">Puesto</th><th className="rb-l"><span className="rb-ia"><Sparkles size={12} /> Predicción IA</span></th><th className="rb-l">Grupo</th><th>Nº personas</th><th>Puntos</th><th className="rb-actions-h"></th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="rb-l rb-strong">{r.nombre}</td>
                  <td className="rb-l rb-soft">{r.bench}</td>
                  <td className="rb-l rb-soft">{r.grupo}</td>
                  <td className="rb-mono">{r.n_personas ?? 0}</td>
                  <td><span className="rb-pts rb-pts-lg">{(r.pts || 0).toLocaleString("es-ES")}</span></td>
                  <td className="rb-actions">
                    <button className="rb-ico-btn" title="Editar" onClick={() => openEdit(r)}><Pencil size={15} /></button>
                    <button className="rb-ico-btn rb-danger" title="Eliminar" onClick={() => setConfirmDel(r)}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td className="rb-l rb-soft" colSpan={6}>No hay puestos.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="rb-modal-bg" onClick={() => setModal(false)}>
          <div className="rb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">{editing ? "Editar puesto" : "Nuevo puesto"}</div><div className="rb-modal-s">Política {session.politica?.ejercicio}</div></div>
              <button className="rb-ico" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b">
              <label>Nombre del puesto</label>
              <input className="rb-inp rb-inp-gap" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="p. ej. Técnico/a de Calidad" />
              <label>Benchmark / código</label>
              <input className="rb-inp rb-inp-gap" value={form.bench} onChange={(e) => setForm({ ...form, bench: e.target.value })} placeholder="p. ej. T05 · Técnico/a de Calidad" />
              <label>Puntos de valoración (VPT)</label>
              <input className="rb-inp rb-inp-gap" type="number" min="0" value={form.pts} onChange={(e) => { const v = e.target.value; const n = parseFloat(v); setForm({ ...form, pts: Number.isNaN(n) ? v : n }); }} />
              {formErr && <div className="rb-formerr">{formErr}</div>}
            </div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
              <button className="rb-btn" disabled={saving} onClick={save}>{saving ? "Guardando…" : (editing ? "Guardar cambios" : "Crear puesto")}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="rb-modal-bg" onClick={() => setConfirmDel(null)}>
          <div className="rb-modal rb-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">Eliminar puesto</div><div className="rb-modal-s">Esta acción no se puede deshacer.</div></div>
              <button className="rb-ico" onClick={() => setConfirmDel(null)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b"><p className="rb-soft">¿Seguro que quieres eliminar <b>{confirmDel.nombre}</b>?</p></div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setConfirmDel(null)}>Cancelar</button>
              <button className="rb-btn rb-btn-danger" disabled={deleting} onClick={remove}>{deleting ? "Eliminando…" : "Eliminar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { IgualdadPersonas, IgualdadPuestos };
